import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useNavigate } from 'react-router-dom';
import { WarningCircle, MagnifyingGlass, CheckSquare, ShieldCheck, Bell, AirplaneTilt, Anchor, CheckCircle, XCircle, UserCirclePlus, Eye, Phone, Globe, ChatCircleDots } from '@phosphor-icons/react';
import { EquipmentAlerts } from '../components/dashboard/EquipmentAlerts';
import { AnalyticsCharts } from '../components/dashboard/AnalyticsCharts';
import { RecentActivity } from '../components/dashboard/RecentActivity';

const API = '/api';

const SOURCE_LABELS = { call: 'Call', website: 'Web', whatsapp: 'WhatsApp' };
const SOURCE_ICONS = { call: Phone, website: Globe, whatsapp: ChatCircleDots };

const getSeverityColor = (sev) => {
  const map = { critical: 'bg-red-600 text-white', high: 'bg-red-100 text-red-800', medium: 'bg-amber-100 text-amber-800', low: 'bg-green-100 text-green-800' };
  return map[sev] || 'bg-slate-100 text-slate-800';
};

const getStatusColor = (status) => {
  const map = { reported: 'bg-amber-100 text-amber-800', approved: 'bg-green-100 text-green-800', rejected: 'bg-red-100 text-red-800', initiated: 'bg-amber-100 text-amber-800', team_assignment: 'bg-blue-100 text-blue-800', in_progress: 'bg-purple-100 text-purple-800', completed: 'bg-green-100 text-green-800' };
  return map[status] || 'bg-slate-100 text-slate-800';
};

const getWorkloadColor = (wl) => {
  if (wl === 0) return 'text-green-600';
  if (wl <= 2) return 'text-amber-600';
  return 'text-red-600';
};

const KPI_CONFIG = [
  { label: 'Total Incidents', key: 'total_incidents', icon: WarningCircle, bgColor: 'bg-red-50', iconColor: 'text-red-600' },
  { label: 'Pending Review', key: 'pending_incidents', icon: Bell, bgColor: 'bg-amber-50', iconColor: 'text-amber-600' },
  { label: 'Active Investigations', key: 'active_investigations', icon: MagnifyingGlass, bgColor: 'bg-blue-50', iconColor: 'text-blue-600' },
  { label: 'Pending Tasks', key: 'pending_tasks', icon: CheckSquare, bgColor: 'bg-purple-50', iconColor: 'text-purple-600' },
  { label: 'Open Recommendations', key: 'open_recommendations', icon: ShieldCheck, bgColor: 'bg-green-50', iconColor: 'text-green-600' },
];

function KpiCards({ stats }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-6 mb-6 sm:mb-8">
      {KPI_CONFIG.map(card => (
        <div key={card.key} className="bg-white rounded-xl p-4 sm:p-6 border border-slate-200 hover:shadow-lg transition-shadow text-center" data-testid={`kpi-${card.label.toLowerCase().replace(/\s+/g, '-')}`}>
          <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg ${card.bgColor} flex items-center justify-center mb-3 sm:mb-4 mx-auto`}>
            <card.icon size={20} weight="duotone" className={card.iconColor} />
          </div>
          <div className="text-2xl sm:text-3xl font-bold text-slate-900 mb-0.5 sm:mb-1" style={{ fontFamily: 'Outfit' }}>{stats[card.key] ?? 0}</div>
          <div className="text-xs sm:text-sm text-slate-500 font-medium">{card.label}</div>
        </div>
      ))}
    </div>
  );
}

function PendingNotifications({ incidents, onReview, onDetail, reviewingId, setReviewingId, reviewRemarks, setReviewRemarks, reviewLoading }) {
  if (!incidents.length) return null;

  return (
    <div className="bg-white rounded-xl p-6 border-2 border-amber-200 mb-6" data-testid="pending-notifications">
      <div className="flex items-center gap-2 mb-4">
        <Bell size={22} weight="fill" className="text-amber-500" />
        <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Outfit' }}>
          Pending Notifications
          <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-amber-100 text-amber-800 font-bold">{incidents.length}</span>
        </h2>
      </div>
      <div className="space-y-3">
        {incidents.map(inc => {
          const SourceIcon = SOURCE_ICONS[inc.notification_source] || Globe;
          const isReviewing = reviewingId === inc.id;
          const identifier = inc.sector === 'aviation'
            ? (inc.aircraft_registration || inc.flight_number || '')
            : (inc.vessel_name || inc.imo_number || '');
          return (
            <div key={inc.id} className="border border-slate-200 rounded-lg overflow-hidden" data-testid={`pending-inc-${inc.id}`}>
              <div className="flex items-center justify-between p-4 hover:bg-slate-50">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  {inc.sector === 'aviation' ? <AirplaneTilt size={20} className="text-blue-500 shrink-0" /> : <Anchor size={20} className="text-cyan-500 shrink-0" />}
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-sm font-semibold text-blue-600">{inc.incident_number}</span>
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${inc.sector === 'aviation' ? 'bg-blue-100 text-blue-700' : 'bg-cyan-100 text-cyan-700'}`}>{inc.sector === 'aviation' ? 'AVN' : 'MAR'}</span>
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${getSeverityColor(inc.severity)}`}>{inc.severity}</span>
                      <span className="capitalize text-xs text-slate-600">{inc.incident_type?.replace('_', ' ')}</span>
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5 truncate">
                      <SourceIcon size={12} className="inline mr-1" weight="bold" />
                      {inc.location} {identifier && `| ${identifier}`} | {inc.date_occurred}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-3 shrink-0">
                  <Button size="sm" variant="outline" className="rounded-lg h-8 w-8 p-0" onClick={() => onDetail(inc)} data-testid={`detail-inc-${inc.id}`}>
                    <Eye size={14} />
                  </Button>
                  {!isReviewing ? (
                    <>
                      <Button size="sm" className="rounded-lg h-8 px-3 bg-green-600 hover:bg-green-700 text-white text-xs" onClick={() => onReview(inc.id, 'approve')} data-testid={`dash-approve-${inc.id}`}>
                        <CheckCircle size={14} className="mr-1" /> Approve
                      </Button>
                      <Button size="sm" variant="outline" className="rounded-lg h-8 px-3 border-red-300 text-red-600 hover:bg-red-50 text-xs" onClick={() => setReviewingId(inc.id)} data-testid={`dash-reject-start-${inc.id}`}>
                        <XCircle size={14} className="mr-1" /> Reject
                      </Button>
                    </>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Textarea value={reviewRemarks} onChange={(e) => setReviewRemarks(e.target.value)} placeholder="Rejection reason..." className="rounded-lg h-8 min-h-[32px] w-48 text-xs py-1 resize-none" data-testid={`dash-reject-remarks-${inc.id}`} />
                      <Button size="sm" className="rounded-lg h-8 px-3 bg-red-500 hover:bg-red-600 text-white text-xs" disabled={reviewLoading} onClick={() => onReview(inc.id, 'reject')} data-testid={`dash-reject-confirm-${inc.id}`}>
                        Confirm
                      </Button>
                      <Button size="sm" variant="outline" className="rounded-lg h-8 px-2 text-xs" onClick={() => { setReviewingId(null); setReviewRemarks(''); }}>
                        Cancel
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function UnassignedInvestigations({ investigations, onAssign }) {
  if (!investigations.length) return null;

  return (
    <div className="bg-white rounded-xl p-6 border-2 border-blue-200 mb-6" data-testid="unassigned-investigations">
      <div className="flex items-center gap-2 mb-4">
        <UserCirclePlus size={22} weight="fill" className="text-blue-500" />
        <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Outfit' }}>
          Awaiting Assignment
          <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-800 font-bold">{investigations.length}</span>
        </h2>
      </div>
      <div className="space-y-3">
        {investigations.map(inv => (
          <div key={inv.id} className="flex items-center justify-between p-4 border border-slate-200 rounded-lg hover:bg-slate-50" data-testid={`unassigned-inv-${inv.id}`}>
            <div className="flex items-center gap-3">
              {inv.sector === 'aviation' ? <AirplaneTilt size={20} className="text-blue-500" /> : <Anchor size={20} className="text-cyan-500" />}
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-sm font-semibold text-blue-600">{inv.investigation_number}</span>
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${inv.sector === 'aviation' ? 'bg-blue-100 text-blue-700' : 'bg-cyan-100 text-cyan-700'}`}>{inv.sector === 'aviation' ? 'AVN' : 'MAR'}</span>
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${getSeverityColor(inv.priority)}`}>{inv.priority}</span>
                </div>
                <div className="text-xs text-slate-500 mt-0.5">Incident: {inv.incident_number} | Created: {inv.created_at ? new Date(inv.created_at).toLocaleDateString() : '-'}</div>
              </div>
            </div>
            <Button size="sm" className="rounded-lg h-9 px-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white text-xs" onClick={() => onAssign(inv)} data-testid={`dash-assign-${inv.id}`}>
              <UserCirclePlus size={14} className="mr-1.5" /> Assign Investigator
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canReview = user?.role === 'admin' || user?.role === 'iic';

  const [stats, setStats] = useState({ total_incidents: 0, pending_incidents: 0, active_investigations: 0, pending_tasks: 0, my_tasks: 0, open_recommendations: 0 });
  const [pendingIncidents, setPendingIncidents] = useState([]);
  const [allIncidents, setAllIncidents] = useState([]);
  const [unassignedInvs, setUnassignedInvs] = useState([]);
  const [allInvestigations, setAllInvestigations] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [equipAlerts, setEquipAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Review state
  const [reviewingId, setReviewingId] = useState(null);
  const [reviewRemarks, setReviewRemarks] = useState('');
  const [reviewLoading, setReviewLoading] = useState(false);

  // Detail view
  const [detailIncident, setDetailIncident] = useState(null);

  // Assign state
  const [assignInv, setAssignInv] = useState(null);
  const [investigators, setInvestigators] = useState([]);
  const [assignLoading, setAssignLoading] = useState(false);

  const loadDashboard = useCallback(async () => {
    try {
      const [statsRes, incRes, invRes, analyticsRes, alertsRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`, { withCredentials: true }),
        axios.get(`${API}/incidents`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/investigations`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/dashboard/analytics`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${API}/dashboard/equipment-alerts`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setStats(statsRes.data);
      setAllIncidents(incRes.data);
      setPendingIncidents(incRes.data.filter(i => i.status === 'reported'));
      setAllInvestigations(invRes.data);
      setUnassignedInvs(invRes.data.filter(i => !i.lead_investigator));
      setAnalytics(analyticsRes.data);
      setEquipAlerts(alertsRes.data);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  const handleReview = async (incidentId, action) => {
    setReviewLoading(true);
    try {
      await axios.put(`${API}/incidents/${incidentId}/review`, { action, remarks: reviewRemarks }, { withCredentials: true });
      setReviewingId(null);
      setReviewRemarks('');
      loadDashboard();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error reviewing incident');
    } finally {
      setReviewLoading(false);
    }
  };

  const openAssign = async (inv) => {
    setAssignInv(inv);
    setAssignLoading(true);
    try {
      const { data } = await axios.get(`${API}/investigations/investigators/availability?sector=${inv.sector}`, { withCredentials: true });
      setInvestigators(data);
    } catch (e) {
      console.error('Failed to load investigators:', e);
      setInvestigators([]);
    } finally {
      setAssignLoading(false);
    }
  };

  const handleAssign = async (email) => {
    try {
      await axios.put(`${API}/investigations/${assignInv.id}/assign`, { investigator_email: email }, { withCredentials: true });
      setAssignInv(null);
      navigate(`/investigations/${assignInv.id}`);
    } catch (error) {
      alert(error.response?.data?.detail || 'Error assigning investigator');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-full"><div className="text-slate-500 font-medium">Loading dashboard...</div></div>;
  }

  return (
    <div className="" data-testid="dashboard">
      {/* Header */}
      <div className="mb-6 sm:mb-8 p-5 sm:p-8 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl text-white">
        <h1 className="text-xl sm:text-3xl font-bold mb-1 sm:mb-2" style={{ fontFamily: 'Outfit' }}>Welcome back, {user?.name}!</h1>
        <p className="text-blue-100 text-xs sm:text-base">RootTrace - Root Cause Intelligence Platform - ICAO 9756 Annex 13</p>
      </div>

      <KpiCards stats={stats} />
      <EquipmentAlerts alerts={equipAlerts} />
      <AnalyticsCharts analytics={analytics} />

      {canReview && (
        <>
          <PendingNotifications
            incidents={pendingIncidents}
            onReview={handleReview}
            onDetail={setDetailIncident}
            reviewingId={reviewingId}
            setReviewingId={setReviewingId}
            reviewRemarks={reviewRemarks}
            setReviewRemarks={setReviewRemarks}
            reviewLoading={reviewLoading}
          />
          <UnassignedInvestigations investigations={unassignedInvs} onAssign={openAssign} />
        </>
      )}

      <RecentActivity incidents={allIncidents} investigations={allInvestigations} />

      {/* Incident Detail Dialog */}
      <Dialog open={!!detailIncident} onOpenChange={(open) => { if (!open) setDetailIncident(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          {detailIncident && (
            <>
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Outfit' }}>
                  {detailIncident.incident_number}
                  <span className={`ml-3 px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(detailIncident.status)}`}>{detailIncident.status}</span>
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><span className="text-xs text-slate-500 uppercase block">Sector</span><span className="font-medium capitalize">{detailIncident.sector}</span></div>
                  <div><span className="text-xs text-slate-500 uppercase block">Source</span><span className="font-medium">{SOURCE_LABELS[detailIncident.notification_source]}</span></div>
                  <div><span className="text-xs text-slate-500 uppercase block">Event Type</span><span className="font-medium capitalize">{detailIncident.incident_type?.replace('_', ' ')}</span></div>
                  <div><span className="text-xs text-slate-500 uppercase block">Severity</span><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getSeverityColor(detailIncident.severity)}`}>{detailIncident.severity}</span></div>
                  <div><span className="text-xs text-slate-500 uppercase block">Date/Time</span><span className="font-medium">{detailIncident.date_occurred} {detailIncident.time_occurred && `at ${detailIncident.time_occurred} UTC`}</span></div>
                  <div><span className="text-xs text-slate-500 uppercase block">Location</span><span className="font-medium">{detailIncident.location}</span></div>
                </div>
                {detailIncident.reporter_name && (
                  <div className="bg-slate-50 p-3 rounded-lg text-sm">
                    <span className="text-xs text-slate-500 uppercase block mb-1">Reporter</span>
                    {detailIncident.reporter_name} {detailIncident.reporter_phone && `| ${detailIncident.reporter_phone}`} {detailIncident.reporter_organization && `| ${detailIncident.reporter_organization}`}
                  </div>
                )}
                {detailIncident.sector === 'aviation' ? (
                  (detailIncident.aircraft_registration || detailIncident.aircraft_type) && (
                    <div className="bg-blue-50 p-3 rounded-lg text-sm">
                      <span className="text-xs text-blue-600 uppercase block mb-1">Aircraft</span>
                      <div className="grid grid-cols-2 gap-2">
                        {detailIncident.aircraft_registration && <div><span className="text-slate-500">Reg:</span> {detailIncident.aircraft_registration}</div>}
                        {detailIncident.aircraft_type && <div><span className="text-slate-500">Type:</span> {detailIncident.aircraft_type}</div>}
                        {detailIncident.flight_number && <div><span className="text-slate-500">Flight:</span> {detailIncident.flight_number}</div>}
                        {detailIncident.operator_name && <div><span className="text-slate-500">Operator:</span> {detailIncident.operator_name}</div>}
                      </div>
                    </div>
                  )
                ) : (
                  (detailIncident.vessel_name || detailIncident.vessel_type) && (
                    <div className="bg-cyan-50 p-3 rounded-lg text-sm">
                      <span className="text-xs text-cyan-600 uppercase block mb-1">Vessel</span>
                      <div className="grid grid-cols-2 gap-2">
                        {detailIncident.vessel_name && <div><span className="text-slate-500">Name:</span> {detailIncident.vessel_name}</div>}
                        {detailIncident.vessel_type && <div><span className="text-slate-500">Type:</span> {detailIncident.vessel_type}</div>}
                        {detailIncident.imo_number && <div><span className="text-slate-500">IMO:</span> {detailIncident.imo_number}</div>}
                        {detailIncident.vessel_flag && <div><span className="text-slate-500">Flag:</span> {detailIncident.vessel_flag}</div>}
                      </div>
                    </div>
                  )
                )}
                <div>
                  <span className="text-xs text-slate-500 uppercase block mb-1">Description</span>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{detailIncident.description}</p>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Assign Investigator Dialog */}
      <Dialog open={!!assignInv} onOpenChange={(open) => { if (!open) setAssignInv(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit' }}>
              Assign Lead Investigator
              {assignInv && <span className="text-sm font-normal text-slate-500 ml-2">for {assignInv.investigation_number}</span>}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg">
              <p className="text-sm text-blue-900">Sorted by workload (least busy first). Select based on availability and specializations.</p>
            </div>
            {assignLoading ? (
              <div className="text-center py-8 text-slate-500">Loading investigators...</div>
            ) : investigators.length === 0 ? (
              <div className="text-center py-8 text-slate-400">No available investigators for this sector.</div>
            ) : (
              <div className="space-y-2">
                {investigators.map(inv => (
                  <div key={inv.email} className="flex items-center justify-between p-4 border border-slate-200 rounded-lg hover:border-blue-300 hover:bg-blue-50/50 transition-all" data-testid={`dash-inv-option-${inv.email}`}>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-slate-900">{inv.name}</span>
                        <span className="px-2 py-0.5 rounded-full text-xs bg-slate-100 text-slate-600 capitalize">{inv.role}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs ${inv.sector === 'aviation' ? 'bg-blue-100 text-blue-800' : inv.sector === 'maritime' ? 'bg-cyan-100 text-cyan-800' : 'bg-purple-100 text-purple-800'}`}>{inv.sector}</span>
                      </div>
                      <div className="text-xs text-slate-500 mt-1">{inv.email}</div>
                      {inv.specializations?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          {inv.specializations.slice(0, 3).map(s => (
                            <span key={s} className="px-1.5 py-0.5 text-[10px] bg-blue-50 text-blue-700 rounded">{s}</span>
                          ))}
                          {inv.specializations.length > 3 && <span className="text-[10px] text-slate-400">+{inv.specializations.length - 3} more</span>}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-4 ml-4">
                      <div className="text-center">
                        <div className={`text-lg font-bold ${getWorkloadColor(inv.workload)}`} style={{ fontFamily: 'Outfit' }}>{inv.workload}</div>
                        <div className="text-[10px] text-slate-500 uppercase">Workload</div>
                      </div>
                      <div className="text-center text-xs text-slate-500">
                        <div>{inv.active_investigations} inv</div>
                        <div>{inv.active_tasks} tasks</div>
                      </div>
                      <Button size="sm" className="rounded-lg bg-gradient-to-r from-blue-600 to-purple-600 text-white" onClick={() => handleAssign(inv.email)} data-testid={`dash-assign-btn-${inv.email}`}>
                        Assign
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
